import type { Package, PaymentQRCode, Sdk360PaymentConfig } from '../../types'
import defaultSdk360Url from '../../assets/SDK360.js?url'

interface Sdk360Runtime {
  init: (config: { appid: string; qid: number; mid: string; m2: string }) => void
  createOrder: (
    params: Sdk360CreateOrderParams,
    responseCallback: (response: Sdk360CreateOrderResponse) => void,
    statusCallback?: (status: unknown) => void
  ) => void
}

export interface Sdk360CreateOrderParams {
  order_id: string
  order_amount: number
  create_time: string
  product_id: string
  product_name: string
  user_id: string
  order_pay_type?: number
  [key: string]: string | number | undefined
}

export interface Sdk360CreateOrderResponse {
  errno?: number
  errmsg?: string
  data?: {
    order_id?: string
    qr_code?: string
    expire_time?: number | string
    remainder?: number | string
    [key: string]: unknown
  }
  [key: string]: unknown
}

export interface Sdk360PaymentMetadata {
  enabled: true
  orderId: string
  qrCode: string
  expireTime?: number
  request: Sdk360CreateOrderParams
  response: Sdk360CreateOrderResponse
}

const SDK360_SCRIPT_ID = 'csd-public-sdk360-script'
let sdk360LoadPromise: Promise<void> | null = null

export function isSdk360PaymentConfigured(
  config?: Sdk360PaymentConfig
): config is Sdk360PaymentConfig & { enabled: true } {
  return config?.enabled === true
}

export async function createSdk360PaymentQRCode(
  standardQRCode: PaymentQRCode,
  pricePackage: Package | undefined,
  config: Sdk360PaymentConfig | undefined,
  softId: string
): Promise<PaymentQRCode & { sdk360?: Sdk360PaymentMetadata }> {
  if (!isSdk360PaymentConfigured(config)) {
    return standardQRCode
  }

  const sdk360Config = config
  const runtime = await ensureSdk360Runtime(sdk360Config)
  runtime.init({
    appid: assertString(sdk360Config.appid, 'payment.sdk360.appid'),
    qid: assertNumber(sdk360Config.qid, 'payment.sdk360.qid'),
    mid: sdk360Config.mid || '',
    m2: sdk360Config.m2 || ''
  })

  const request = buildSdk360CreateOrderParams(standardQRCode, pricePackage, sdk360Config, softId)
  const response = await requestSdk360CreateOrder(runtime, request, sdk360Config.timeoutMs)
  const qrCode = response.data?.qr_code
  const expireTime = parseSdk360ExpireTime(response.data?.expire_time, response.data?.remainder)

  if (!qrCode) {
    throw new Error(response.errmsg || '360 支付返回缺少二维码地址')
  }

  const sdk360: Sdk360PaymentMetadata = {
    enabled: true,
    orderId: request.order_id,
    qrCode,
    expireTime,
    request,
    response
  }

  return {
    ...standardQRCode,
    url: qrCode,
    order_url: qrCode,
    qr_code_url: qrCode,
    qr_code_content: qrCode,
    expire_at: expireTime ? new Date(expireTime * 1000).toISOString() : standardQRCode.expire_at,
    sdk360
  }
}

function parseSdk360ExpireTime(
  expireTime: number | string | undefined,
  remainder: number | string | undefined
): number | undefined {
  if (typeof expireTime === 'number' && Number.isFinite(expireTime) && expireTime > 0) {
    return normalizeTimestamp(expireTime)
  }

  if (typeof expireTime === 'string' && expireTime.trim()) {
    const value = expireTime.trim()
    const numeric = Number(value)
    if (Number.isFinite(numeric) && numeric > 0) {
      return normalizeTimestamp(numeric)
    }

    const parsed = Date.parse(value.replace(' ', 'T'))
    if (Number.isFinite(parsed)) {
      return Math.floor(parsed / 1000)
    }
  }

  const seconds = Number(remainder)
  if (Number.isFinite(seconds) && seconds > 0) {
    return Math.floor(Date.now() / 1000) + Math.floor(seconds)
  }

  return undefined
}

function normalizeTimestamp(value: number): number {
  return value > 9999999999 ? Math.floor(value / 1000) : Math.floor(value)
}

function buildSdk360CreateOrderParams(
  data: PaymentQRCode,
  pricePackage: Package | undefined,
  config: Sdk360PaymentConfig | undefined,
  softId: string
): Sdk360CreateOrderParams {
  const orderId = String(data.order_sn || data.order_id || '')
  const amount = Math.round(Number(data.price || data.amount || 0) * 100)
  const productId = String(pricePackage?.product_id || config?.productId || softId)
  const productName = pricePackage?.product_name || pricePackage?.plan_name || config?.productName

  if (!orderId) {
    throw new Error('360 支付缺少订单号')
  }

  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('360 支付订单金额无效')
  }

  const params: Sdk360CreateOrderParams = {
    order_id: orderId,
    order_amount: amount,
    create_time: String(Math.floor(Date.now() / 1000)),
    product_id: productId,
    product_name: productName || '会员套餐',
    user_id: config?.userId || '0',
    ...config?.extraParams
  }

  if (config?.orderPayType !== undefined) {
    params.order_pay_type = config.orderPayType
  }

  return params
}

async function ensureSdk360Runtime(config: Sdk360PaymentConfig): Promise<Sdk360Runtime> {
  const existing = getSdk360Runtime()
  if (existing) return existing

  if (config.autoLoad === false) {
    throw new Error('360 支付 SDK 未加载')
  }

  await loadSdk360Script(config.sdkUrl || defaultSdk360Url)

  const runtime = getSdk360Runtime()
  if (!runtime) {
    throw new Error('360 支付 SDK 加载后未发现 SDK360 对象')
  }
  return runtime
}

function getSdk360Runtime(): Sdk360Runtime | null {
  const runtime = (globalThis as any).SDK360
  if (runtime?.init && runtime?.createOrder) {
    return runtime
  }
  return null
}

function loadSdk360Script(sdkUrl: string): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('当前环境不支持加载 360 支付 SDK'))
  }

  if (sdk360LoadPromise) return sdk360LoadPromise

  sdk360LoadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SDK360_SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('360 支付 SDK 加载失败')), {
        once: true
      })
      return
    }

    const script = document.createElement('script')
    script.id = SDK360_SCRIPT_ID
    script.src = sdkUrl
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('360 支付 SDK 加载失败'))
    document.head.appendChild(script)
  })

  return sdk360LoadPromise
}

function requestSdk360CreateOrder(
  runtime: Sdk360Runtime,
  params: Sdk360CreateOrderParams,
  timeoutMs = 15000
): Promise<Sdk360CreateOrderResponse> {
  return new Promise((resolve, reject) => {
    let settled = false
    const timer = window.setTimeout(() => {
      if (!settled) {
        settled = true
        reject(new Error('360 支付出码超时'))
      }
    }, timeoutMs)

    runtime.createOrder(
      params,
      (response) => {
        if (settled) return
        settled = true
        window.clearTimeout(timer)

        if (response?.errno !== 0) {
          reject(new Error(response?.errmsg || '360 支付出码失败'))
          return
        }

        resolve(response)
      },
      () => {}
    )
  })
}

function assertString(value: unknown, name: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${name} 未配置`)
  }
  return value
}

function assertNumber(value: unknown, name: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new Error(`${name} 未配置`)
  }
  return value
}
